import { useCallback, useEffect, useMemo, useRef } from 'react';

import { usePetitionActionsPageData, usePetitionActionsPageState } from '#src/app/pages/actions/pageContext';
import { useSupporterVoicesVideoFirstTrackingProps } from '#src/app/pages/actions/shared/hooks';
import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';

import { useVideoContext, useVideosComponentContext } from '../../../../../../context';
import { useCamera } from '../../../../../../shared/hooks/useCamera';
import { useFinishTake } from '../../../../../../shared/hooks/useFinishTake';
import { useRecordingDuration } from '../../../../../../shared/hooks/useRecordingDuration';
import { useVideoRecorderActions } from '../../../../../../shared/hooks/useVideoRecorderActions';
import { MAX_DURATION_SEC } from '../../../../../VideoRecorder/components/RecorderTimeLimit';
import { useUploadProcess } from '../../../../../VideoUpload/components/Upload/hooks/useUploadProcess';
import { useSkipRecording } from '../../../SkipRecording/hooks/useSkipRecording';
import { getLocationLabel } from '../../../VideoFirstAsk/hooks/useVideoFirstAsk/getLocationLabel';

/**
 * Where the supporter is on the one screen this arm has.
 *
 *  - `prime`     nothing granted yet; the montage is playing
 *  - `live`      camera on, nothing rolling
 *  - `recording`
 *  - `review`    a take exists, waiting to be submitted, re-done or deleted
 */
export type ConsolidatedAskPhase = 'prime' | 'live' | 'recording' | 'review';

function derivePhase({
	isRecording,
	blob,
	stream,
}: {
	isRecording: boolean;
	blob: Blob | null;
	stream: MediaStream | null;
}): ConsolidatedAskPhase {
	// `isRecording` is checked ahead of `blob` so a re-do rolls straight into `recording` while the
	// previous take is still in context - otherwise the screen would drop back to the montage between the
	// two.
	if (isRecording) {
		return 'recording';
	}
	if (blob) {
		return 'review';
	}
	return stream ? 'live' : 'prime';
}

type Result = ModelHookResult<
	{
		phase: ConsolidatedAskPhase;
		location: string | undefined;
		stream: MediaStream | null;
		/** Seconds left in the current take. Counts down, unlike the recorder screen's count up. */
		secondsRemaining: number;
		takeLengthSec: number | undefined;
		blob: Blob | null;
		isUploading: boolean;
		errorMessage: string | null;
	},
	{
		handleRecordTap: () => void;
		handleTryAgain: () => void;
		handleSubmit: () => void;
		handleSkip: () => void;
	}
>;

/**
 * The whole of `v2_consolidated`'s ask: permission, capture, review and submit, without a screen change.
 *
 * The phase is derived from state the flow already holds rather than tracked separately, so there is no
 * second machine here to fall out of step with `uploadStep` - which stays on `ready` throughout, and is
 * what keeps this screen mounted and outside the action card.
 */
// eslint-disable-next-line max-lines-per-function, max-statements -- max-statements exceeds the 18-statement limit; flagged as documented debt rather than refactored, per team decision
export function useVideoFirstConsolidatedAsk(): Result {
	const { track } = useCampaignTracking();
	const {
		data: {
			petition: { relevantLocationLocalizedName },
		},
	} = usePetitionActionsPageData();
	const [, { setShareSupporterVoiceSuccess }] = usePetitionActionsPageState();
	const {
		data: { isUploading, errorMessage },
		actions: { setErrorMessage },
	} = useVideosComponentContext();
	const {
		data: { blob, recordedDurationSec },
		actions: { setBlob, setStream },
	} = useVideoContext();
	const { openCamera, closeCamera } = useCamera();
	const {
		actions: { handleSkip: trackAndGoToComment, goToCommentStep },
	} = useSkipRecording();
	const {
		actions: { startUploadProcess },
	} = useUploadProcess();
	const trackingProps = useSupporterVoicesVideoFirstTrackingProps();

	// One instance for the whole screen: `useMediaRecorder`'s own `MediaRecorder` reference is local to
	// this hook, so a second caller would hold an independent recorder a `stopRecording()` here could
	// never reach.
	const {
		data: { isRecording, stream },
		actions: { startRecording, stopRecording, setVideoSource },
	} = useVideoRecorderActions();

	// The clock has to be set up before the handler that stops it, because that handler needs the elapsed
	// time the clock is holding. The ref breaks the loop; the clock re-reads it on every fire. Bundled with
	// the double-tap guard below so the two don't cost this hook a second `useRef` statement against the
	// max-statements limit.
	const refs = useRef({ finishTake: () => {}, isRequestingCamera: false });

	const { duration, getElapsedSeconds } = useRecordingDuration({
		isRecording,
		onMaxDuration: () => {
			track('create_video_max_duration_reached');
			refs.current.finishTake();
		},
		onBackgrounded: () => {
			track('create_video_stopped_backgrounded');
			refs.current.finishTake();
		},
	});

	const finishTake = useFinishTake({ stopRecording, getElapsedSeconds, setVideoSource });

	useEffect(() => {
		refs.current.finishTake = finishTake;
	}, [finishTake]);

	/**
	 * The record control means three things across the screen: ask for permission, start, stop. One
	 * button because there is only ever one thing to do with the camera at any point.
	 */
	const handleRecordTap = useCallback(() => {
		if (isRecording) {
			track('campaign_action_create_video_stop_recording_click');
			finishTake();
			return;
		}

		if (stream) {
			track('campaign_action_create_video_start_recording_click');
			void startRecording();
			return;
		}

		if (refs.current.isRequestingCamera) {
			return;
		}

		const requestPermission = async () => {
			refs.current.isRequestingCamera = true;
			track('campaign_action_create_video_add_video_click', { source: 'campaign_action_landing', ...trackingProps });
			setShareSupporterVoiceSuccess(false);
			setBlob(null);

			try {
				const mediaStream = await openCamera();
				if (mediaStream) {
					return;
				}

				// `openCamera` leaves behind an explanation of how to grant permission, which belongs on a
				// recorder, not on the comment form the supporter is about to see.
				setErrorMessage(null);
				goToCommentStep();
			} finally {
				refs.current.isRequestingCamera = false;
			}
		};

		void requestPermission();
	}, [
		finishTake,
		goToCommentStep,
		isRecording,
		openCamera,
		setBlob,
		setErrorMessage,
		setShareSupporterVoiceSuccess,
		startRecording,
		stream,
		track,
		trackingProps,
	]);

	/**
	 * Back to a live camera that is waiting, not to a camera already rolling.
	 *
	 * This control wears production's "Try again" label and sits where production puts it, and there it
	 * returns the supporter to an armed recorder they then start themselves. Rolling on the tap would film
	 * someone who pressed it expecting to get ready. Untracked, as production's retry is - and firing the
	 * start-recording event here would count taps that started no recording.
	 *
	 * The camera is reopened before the take is cleared, so the surface holds the take it was showing
	 * rather than flashing the montage back in while permission resolves. A failure to reopen leaves the
	 * take in place, with the explanation `openCamera` writes rendered above it.
	 */
	const handleTryAgain = useCallback(() => {
		const reopen = async () => {
			const mediaStream = await openCamera();
			if (!mediaStream) {
				return;
			}
			setBlob(null);
		};

		void reopen();
	}, [openCamera, setBlob]);

	// This arm submits from its own inline ask rather than through `Upload`, which passes the same props
	// on the same event - miss them here and `v2_consolidated` is the one arm the submit can't be sliced by.
	const handleSubmit = useCallback(() => {
		track('campaign_action_create_video_submit_click', trackingProps);
		startUploadProcess();
	}, [startUploadProcess, track, trackingProps]);

	const handleSkip = useCallback(() => {
		closeCamera();
		setStream(null);
		trackAndGoToComment();
	}, [closeCamera, setStream, trackAndGoToComment]);

	const location = useMemo(() => getLocationLabel(relevantLocationLocalizedName), [relevantLocationLocalizedName]);

	return {
		data: {
			phase: derivePhase({ isRecording, blob, stream }),
			location,
			stream,
			secondsRemaining: Math.max(MAX_DURATION_SEC - (duration ?? 0), 0),
			takeLengthSec: recordedDurationSec,
			blob,
			isUploading,
			errorMessage,
		},
		actions: { handleRecordTap, handleTryAgain, handleSubmit, handleSkip },
	};
}
